import { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { NewSubscriptionApi } from 'api/newSubscriptionApi';
import { Elements, injectStripe } from 'react-stripe-elements';
import { PaymentTypes } from 'appConstants';
import { formatNumber } from 'utils/formating';
import { RenewSubscriptionFormValues, SubscriptionsParser } from './models';
import { PopoutMenu } from 'components/popupsAndPanels/PopoutMenu';
import { PopoutMenuFooter } from 'components/popupsAndPanels/PopoutMenuFooter';
import { PopoutMenuHeader } from 'components/popupsAndPanels/PopoutMenuHeader';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import FormTextField from 'components/formComponents/FormTextField';
import FormStripeCardField from 'components/formComponents/FormStripeCardField';
import { useFetchRenewalSubscription } from 'hooks/useFetchRenewalSubscription';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import { useToast } from 'hooks/useToast';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  menuTitle: string;
  id: number;
  packageId: number;
  stripe?: any;
  setUpdateList: React.Dispatch<React.SetStateAction<boolean>>;
};

interface PriceInfo {
  price: number;
  vat: number;
  total: number;
}

const FORM_FIELD_PLACEHOLDER = 'Enter PO number (optional)';

const InnerRenewSubscriptions: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  menuTitle,
  id,
  packageId,
  stripe,
  setUpdateList,
}) => {
  const handlePayByCardToast = useToast('Your payment was successful');
  const handlePayByInvoiceToast = useToast(
    'You will shortly be contacted by email'
  );

  const [promoCode, setPromoCode] = useState({ value: '', error: '' });
  const [promoCodeList, setPromoCodeList] = useState<any>([]);
  const [priceInfo, setPriceInfo] = useState<PriceInfo>({
    price: 0,
    vat: 0,
    total: 0,
  });
  const [vatRate, setVatRate] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [packageIdState, setPackageIdState] = useState<number>(0);
  const [financeEmail, setFinanceEmail] = useState<any>('');

  // This is the state for the initial subscription option, the fetch over rides this and then formik handle colouring of cells.
  const [subscriptionOption, setSubscriptionOption] = useState('');

  // This gets the subscription information of the user
  // The fetched object will contain the subscriptionDurationOptions[]
  const { subscription, error: subscriptionError } =
    useFetchRenewalSubscription(id);

  useEffect(() => {
    if (!subscription) return;
    setVatRate(subscription.details.vatRate);
    const [vat, total] = calculatePrice(
      subscription.details.price,
      subscription.details.vatRate
    );

    setPriceInfo({ price: subscription.details.price, vat, total });
    setSubscriptionOption(subscription.details.type);
    setPackageIdState(subscription.details.packageId);
    setFinanceEmail(subscription.details.financeEmail);
  }, [subscription]);

  // When a user selects a subscription type, update the price.
  const handlePriceChange = (PackageId: number) => {
    let newPrice = 0;

    subscription.details.subscriptionTypes.forEach((obj: any) => {
      if (obj.packageId === PackageId) {
        newPrice = obj.price;
      }
    });

    // Recalculate pricing information.
    const [vat, newTotal] = calculatePrice(
      newPrice,
      subscription.details.vatRate
    );

    setPriceInfo({ price: newPrice, vat, total: newTotal });
  };

  const calculatePrice = (price: number, vatRate: number) => {
    const vat = price * (vatRate / 100);

    const total = price + vat;

    return [vat, total];
  };

  const handleMenuClose = () => {
    togglePanelOpen(false);
  };

  const applyPromoCode = async (
    values: RenewSubscriptionFormValues,
    setFieldValue: (fieldName: string, value: any) => void,
    promoCodeVal: string,
    packageId: number
  ) => {
    const result = await NewSubscriptionApi.checkPromoCode(promoCodeVal);
    if (result.data) {
      const packages = result.data.generalPackages
        .map((i: any) => i.subjects.map((j: any) => j.subscriptions))
        .flat()
        .flat();

      const selectedPackage = packages.find(
        (p: any) => p.packageID === packageId
      );

      const discountedTotalPriceWithoutVAT =
        selectedPackage.price - selectedPackage.promoCodeDiscount;
        
      const discountedTotalPriceWithVAT =
        discountedTotalPriceWithoutVAT +
        discountedTotalPriceWithoutVAT * (result.data.vatRate / 100);

      const discount = selectedPackage.price - discountedTotalPriceWithoutVAT;

      const vatValue =
        discountedTotalPriceWithVAT - discountedTotalPriceWithoutVAT;

      const completeTotal = discountedTotalPriceWithoutVAT + vatValue;

      setPriceInfo({
        price: formatNumber(selectedPackage.price),
        vat: formatNumber(vatValue),
        total: formatNumber(completeTotal),
      });

      setFieldValue('validPromoCode', promoCodeVal);

      setFieldValue('validDiscount', discount);
      setPromoCode({
        value: '',
        error: '',
      });
      const promoCodeListCopy = promoCodeList;
      let addCodeToList = true;
      for(let i = 0; i < promoCodeList.length; i++){
        if(promoCodeList[i] === promoCodeVal){
          addCodeToList = false;
        }
      }
      if(addCodeToList){
        promoCodeListCopy.push(promoCodeVal);
        setPromoCodeList(promoCodeListCopy);
      }
    }

    if (result.error) {
      setPromoCode({ value: '', error: result.error.message });
      setFieldValue('validPromoCode', '');
      setFieldValue('validDiscount', 0);
    }
  };

  const renewByInvoice = async (
    values: RenewSubscriptionFormValues,
    setSubmitting: (status: boolean) => void
  ) => {
    const result = await SubscriptionsApi.renewByInvoice(
      values,
      // [subscription.details.packageId],
      [packageIdState],
      priceInfo.total
    );
    if (result.data) {
      setSubmitting(false);
      handlePayByInvoiceToast();
      setUpdateList(true);
      handleMenuClose();
    }

    if (result.error) {
      setSubmitting(false);
      setError(result.error.message);
    }
  };

  const createStripeToken = (
    values: RenewSubscriptionFormValues,
    setSubmitting: (status: boolean) => void
  ) =>
    stripe
      .createToken({ name: values.cardholderName })
      .then(({ token, error }: any) => {
        if (token) {
          renewByCard(token, values, setSubmitting);
        }
        if (error) {
          setSubmitting(false);
          setError('Sorry, something went wrong. Please try again.');
        }
      });

  const renewByCard = async (
    token: any,
    values: RenewSubscriptionFormValues,
    setSubmitting: (status: boolean) => void
  ) => {
    const result = await SubscriptionsApi.renewByCard(
      values,
      token,
      priceInfo.total,
      // [subscription.details.packageId]
      [packageIdState]
    );
    if (result.data) {
      const status = result.data.status;
      if (status === 'succeeded') {
        handlePayByCardToast();
        setUpdateList(true);
        handleMenuClose();
      }

      if (status === 'requires_action') {
        const clientSecret = result.data.clientSecret;
        const tid = result.data.tid;
        handleCardAction(clientSecret, setSubmitting, tid);
      }
    }
    if (result.error) {
      setError(result.error.message);
      setSubmitting(false);
    }
  };

  const handleCardAction = (
    clientSecret: string,
    setSubmitting: (status: boolean) => void,
    tid: string
  ) => {
    stripe.handleCardAction(clientSecret).then((res: any) => {
      if (res.error) {
        setSubmitting(false);
      }
      renewAfter3DSecure(res, tid, setSubmitting);
    });
  };

  const renewAfter3DSecure = async (
    values: any,
    tid: string,
    setSubmitting: (status: boolean) => void
  ) => {
    const result = await SubscriptionsApi.renewAfter3DSecureUpgrade({
      ...values,
      tid,
    });

    if (result.error) {
      setError(result.error.message);
      setSubmitting(false);
    } else {
      handlePayByCardToast();
      setUpdateList(true);
      handleMenuClose();
    }
  };

  const handleFormSubmission = (
    values: RenewSubscriptionFormValues,
    { setFieldError, setSubmitting }: FormikHelpers<RenewSubscriptionFormValues>
  ) => {
    if (values.paymentType === PaymentTypes.PAY_BY_CARD) {
      if (!values.cardholderName) {
        setFieldError('cardholderName', 'Please provide the cardholder name');
        setSubmitting(false);
        return;
      }
      if (!values.paymentType) {
        setFieldError('paymentType', 'Please provide a type of payment');
        setSubmitting(false);
        return;
      }
      createStripeToken(values, setSubmitting);
    }
    if (values.paymentType === PaymentTypes.PAY_BY_INVOICE) {
      renewByInvoice(values, setSubmitting);
    }
  };
  
  return (
    <Formik
      initialValues={SubscriptionsParser.createEmptyRenewPackages(
        subscriptionOption,
        financeEmail
      )}
      onSubmit={(values, formikHelpers) =>
        handleFormSubmission(values, formikHelpers)
      }
      validationSchema={Yup.object({
        FinanceEmail: Yup.string().email('Please enter a valid email').required('A finance email is required').nullable(),
      })}
      enableReinitialize={true}
    >
      {({ values, submitForm, isSubmitting, setFieldValue }) => (
        <Form>
          <PopoutMenu
            isPopup={false}
            isOpen={panelOpen}
            handleClose={handleMenuClose}
            className="SubscriptionsPopoutMenu"
            position="right"
            loading={!subscription && !subscriptionError}
          >
            <PopoutMenuHeader title={menuTitle} handleClose={handleMenuClose} />
            <div className="PopoutMenuContent">
              <FormGeneralError message={error} />
              <FormTextField
                field={'poNumber'}
                placeholder={FORM_FIELD_PLACEHOLDER}
                required={false}
              />

              {subscription && (
                <>
                  {/* values.subDuration is the user's pre-existing tarrig (See: createEmptyRenewPackages).
                  The below section allows instant update of the subDuration onClick */}
                  <div className="UpdateSubDurType">
                    {subscription.details.subscriptionTypes.map((obj: any) => {
                      return (
                        <div
                          className={`SubDurType ${
                            values.subDuration === obj.type ? 'Selected' : ''
                          }
                         ${subscriptionOption === obj.type ? 'Selected' : ''}`}
                          onClick={() => {
                            setSubscriptionOption('');
                            setFieldValue('subDuration', obj.type);
                            setPackageIdState(obj.packageId);
                            handlePriceChange(obj.packageId);

                            if(values.validDiscount > 0){
                              for(let i = 0; i < promoCodeList.length; i++){
                                applyPromoCode(values, setFieldValue, promoCodeList[i], obj.packageId);
                              }
                            }
                          }}
                        >
                          {obj.type}
                        </div>
                      );
                    })}
                  </div>
                  <div className="UpdatePaymentType">
                    <div
                      className={`PaymentType ${
                        values.paymentType === PaymentTypes.PAY_BY_CARD
                          ? 'Selected'
                          : ''
                      }`}
                      onClick={() =>
                        setFieldValue('paymentType', PaymentTypes.PAY_BY_CARD)
                      }
                    >
                      {PaymentTypes.PAY_BY_CARD}
                    </div>
                    <div
                      className={`PaymentType ${
                        values.paymentType === PaymentTypes.PAY_BY_INVOICE
                          ? 'Selected'
                          : ''
                      }`}
                      onClick={() =>
                        setFieldValue(
                          'paymentType',
                          PaymentTypes.PAY_BY_INVOICE
                        )
                      }
                    >
                      {PaymentTypes.PAY_BY_INVOICE}
                    </div>
                  </div>
                  <div className="DiscoundContainer">
                    <input
                      className="Discount"
                      type="text"
                      onChange={(e) =>
                        setPromoCode((prevState) => ({
                          ...prevState,
                          value: e.target.value,
                        }))
                      }
                      value={promoCode.value}
                      placeholder="Enter promo code (optional)"
                    />

                    <button
                      className="Btn BtnOrange"
                      type="button"
                      disabled={promoCode.value ? false : true}
                      onClick={() => applyPromoCode(values, setFieldValue, promoCode.value, packageIdState)}
                    >
                      Apply
                    </button>
                  </div>
                  <p>Enter email address to send card receipt / invoice to:</p>
                  <FormTextField
                    field={'FinanceEmail'}
                    placeholder={'Email address'}
                    required={true}
                    value={values.FinanceEmail}
                  />
                  <FormGeneralError message={promoCode.error} />
                  {values.paymentType === PaymentTypes.PAY_BY_CARD && (
                    <>
                      <h4 className="SpaceTopBtm">Payment details </h4>
                      <div className="CardPaymetFields">
                        <FormTextField
                          field={'cardholderName'}
                          label={"Cardholder's name"}
                          labelPosition={'left'}
                          required={true}
                        />

                        <FormStripeCardField
                          field={'card'}
                          label={'Card '}
                          required={true}
                          labelPosition={'left'}
                        />
                      </div>
                    </>
                  )}
                  <div
                    className="TotalDetails"
                    style={{ width: '100%', paddingTop: '30px' }}
                  >
                    <div className="DetailsItem">
                      <p>Price</p>
                      <span>£ {formatNumber(priceInfo.price)}</span>
                    </div>

                    {values.validDiscount > 0 && (
                      <div className="DetailsItem">
                        <p>Discount</p>
                        <span>
                          -£
                          {formatNumber(values.validDiscount)}
                        </span>
                      </div>
                    )}
                    {values.validDiscount > 0 && (
                      <div className="DetailsItem">
                        <p>Discounted price</p>
                        <span>
                          £
                          {formatNumber(priceInfo.price - values.validDiscount)}
                        </span>
                      </div>
                    )}
                    <div className="DetailsItem">
                      <p>VAT ({vatRate}%)</p>
                      <span>£ {formatNumber(priceInfo.vat)}</span>
                    </div>

                    <div className="DetailsItem">
                      <p>Total price</p>
                      <span>£ {formatNumber(priceInfo.total)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <PopoutMenuFooter
              primaryButtonText="Renew"
              secondaryButtonText="Cancel"
              handlePrimaryButtonClick={submitForm}
              handleSecondaryButtonClick={handleMenuClose}
              isSubmitting={isSubmitting}
            />
          </PopoutMenu>
        </Form>
      )}
    </Formik>
  );
};

const RenewSubscriptionsWithStripe = injectStripe(InnerRenewSubscriptions);

export const RenewSubscriptionsForm: React.FC<Props> = (props) => {
  return (
    <Elements>
      <RenewSubscriptionsWithStripe {...props} />
    </Elements>
  );
};
